// Pure save-time validation for the budget editor. Turns the editable drafts
// (category amounts, breakdown lines, income schedules) into the persisted
// Budget payload, or returns the first human-readable error. Kept free of
// Svelte/DOM so the cross-field rules can be unit-tested directly.

import { isWithinRange } from './budget';
import { scheduleItemTotal, type ScheduleItem } from './schedule';
import { getPayCycleRange } from './month';
import { parseAmount, type CategoryLineDraft, type IncomeScheduleDraft } from './budgetDraft';
import { parseDateOnly } from './format';
import type { CategoryLine, IncomeSchedule } from './db';

export const linesError = (category: string): string =>
	`Fill in amount and schedule details for every line in the "${category}" breakdown.`;
export const SCHEDULES_ERROR =
	'Fill in category, amount, and schedule details for all income schedules.';

export interface BudgetPayload {
	plannedIncome?: number;
	categoryBudgets?: Record<string, number>;
	categoryLines?: Record<string, CategoryLine[]>;
	incomeSchedules?: IncomeSchedule[];
}

type Range = { start: Date; end: Date };

// Validate the per-frequency schedule fields shared by lines and income
// schedules, mutating `target` with the rounded/typed value. Returns false on
// the first invalid field.
function applyFrequencyFields(
	draft: { frequency: CategoryLineDraft['frequency']; monthlyDay: string; fortnightlyPayDate: string; fourWeeklyPayDate: string; weeklyDay: string; oneOffDate: string },
	target: { monthlyDay?: number; fortnightlyPayDate?: string; fourWeeklyPayDate?: string; weeklyDay?: number; oneOffDate?: string },
	range: Range
): boolean {
	if (draft.frequency === 'monthly') {
		const day = Number(draft.monthlyDay);
		if (!Number.isFinite(day) || day < 1 || day > 31) return false;
		target.monthlyDay = Math.round(day);
	} else if (draft.frequency === 'fortnightly') {
		if (!draft.fortnightlyPayDate) return false;
		const anchor = parseDateOnly(draft.fortnightlyPayDate);
		if (!isWithinRange(anchor, range.start, range.end)) return false;
		target.fortnightlyPayDate = draft.fortnightlyPayDate;
	} else if (draft.frequency === 'four-weekly') {
		if (!draft.fourWeeklyPayDate) return false;
		const anchor = parseDateOnly(draft.fourWeeklyPayDate);
		if (!isWithinRange(anchor, range.start, range.end)) return false;
		target.fourWeeklyPayDate = draft.fourWeeklyPayDate;
	} else if (draft.frequency === 'weekly') {
		const weekday = Number(draft.weeklyDay);
		if (!Number.isFinite(weekday) || weekday < 0 || weekday > 6) return false;
		target.weeklyDay = Math.round(weekday);
	} else if (draft.frequency === 'one-off') {
		if (!draft.oneOffDate) return false;
		const date = parseDateOnly(draft.oneOffDate);
		if (!isWithinRange(date, range.start, range.end)) return false;
		target.oneOffDate = draft.oneOffDate;
	}
	return true;
}

export interface LinesResult {
	lines: Record<string, CategoryLine[]>;
	/** Computed category totals — overwrite the typed-in amount when lines exist. */
	totals: Record<string, number>;
	error: string | null;
}

/**
 * Validate the category breakdown lines and compute each category's total over
 * the cycle. A draft counts as "real" once it has either an amount or a label;
 * an all-blank draft is ignored. Any malformed real line aborts with a
 * linesError naming the category.
 */
export function buildLinesPayload(
	lineDrafts: Record<string, CategoryLineDraft[]>,
	range: Range
): LinesResult {
	const lines: Record<string, CategoryLine[]> = {};
	const totals: Record<string, number> = {};

	for (const [category, drafts] of Object.entries(lineDrafts)) {
		const realDrafts = drafts.filter((d) => d.amount.trim() !== '' || d.name.trim() !== '');
		if (realDrafts.length === 0) continue;

		const built: CategoryLine[] = [];
		for (const draft of realDrafts) {
			const amount = parseAmount(draft.amount);
			if (amount === null) return { lines: {}, totals: {}, error: linesError(category) };
			const line: CategoryLine = {
				name: draft.name.trim() || undefined,
				amount,
				frequency: draft.frequency,
			};
			if (!applyFrequencyFields(draft, line, range)) {
				return { lines: {}, totals: {}, error: linesError(category) };
			}
			built.push(line);
		}
		if (built.length > 0) {
			lines[category] = built;
			totals[category] = built.reduce(
				(sum, l) => sum + scheduleItemTotal(l as ScheduleItem, range.start, range.end),
				0
			);
		}
	}

	return { lines, totals, error: null };
}

export interface SchedulesResult {
	schedules: IncomeSchedule[];
	error: string | null;
}

/**
 * Validate the income schedules. A wholly-blank schedule (no category, amount,
 * or name) is dropped; any partially-filled or malformed schedule aborts with
 * SCHEDULES_ERROR.
 */
export function buildSchedulesPayload(
	drafts: IncomeScheduleDraft[],
	range: Range
): SchedulesResult {
	const schedules: IncomeSchedule[] = [];
	for (const schedule of drafts) {
		const category = schedule.category.trim();
		const amount = parseAmount(schedule.amount);
		if (!category && !schedule.amount.trim() && !schedule.name.trim()) continue;
		if (!category || amount === null) return { schedules: [], error: SCHEDULES_ERROR };

		const base: IncomeSchedule = {
			name: schedule.name.trim() || undefined,
			category,
			amount,
			frequency: schedule.frequency,
		};
		if (!applyFrequencyFields(schedule, base, range)) {
			return { schedules: [], error: SCHEDULES_ERROR };
		}
		schedules.push(base);
	}
	return { schedules, error: null };
}

/**
 * Assemble the full Budget payload from the editor drafts, or return the first
 * validation error. `categoryBudgets` merges typed-in amounts with line-derived
 * totals (lines win); `plannedIncome` is the validated schedules' cycle total.
 */
export function buildBudgetPayload(
	categoryAmounts: Record<string, string>,
	lineDrafts: Record<string, CategoryLineDraft[]>,
	incomeSchedules: IncomeScheduleDraft[],
	yearMonth: string
): { payload: BudgetPayload; error: null } | { payload: null; error: string } {
	const range = getPayCycleRange(yearMonth);
	const payload: BudgetPayload = {};

	const budgetEntries: Record<string, number> = {};
	for (const [name, value] of Object.entries(categoryAmounts)) {
		const parsed = parseAmount(value);
		if (parsed !== null) budgetEntries[name] = parsed;
	}

	const lineResult = buildLinesPayload(lineDrafts, range);
	if (lineResult.error) return { payload: null, error: lineResult.error };
	for (const [category, total] of Object.entries(lineResult.totals)) {
		budgetEntries[category] = total;
	}

	if (Object.keys(budgetEntries).length > 0) payload.categoryBudgets = budgetEntries;
	if (Object.keys(lineResult.lines).length > 0) payload.categoryLines = lineResult.lines;

	const scheduleResult = buildSchedulesPayload(incomeSchedules, range);
	if (scheduleResult.error) return { payload: null, error: scheduleResult.error };
	if (scheduleResult.schedules.length > 0) {
		payload.incomeSchedules = scheduleResult.schedules;
		payload.plannedIncome = scheduleResult.schedules.reduce(
			(sum, s) => sum + scheduleItemTotal(s as ScheduleItem, range.start, range.end),
			0
		);
	}

	return { payload, error: null };
}
